// Conexiones OAuth activas (usuario ↔ ChatGPT): revocar y probar.
import { useState } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Ban, FlaskConical, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtFechaHora } from './oauthUtils';

export function OAuthConexiones({ conexiones, onReload }) {
  const [incluirRevocadas, setIncluirRevocadas] = useState(false);
  const [prueba, setPrueba] = useState(null);
  const [probando, setProbando] = useState(false);

  const revocar = async (c) => {
    if (!window.confirm(`¿Revocar la conexión de ${c.email} con "${c.client_nombre}"? ChatGPT le pedirá autorizar de nuevo.`)) return;
    try { await api.delete(`/oauth/admin/conexiones/${c.id}`); toast.success('Conexión revocada'); onReload(incluirRevocadas); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo revocar'); }
  };
  const probar = async (grantId) => {
    setProbando(true);
    try { const r = await api.get('/oauth/admin/probar', { params: grantId ? { grant_id: grantId } : {} }); setPrueba(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo probar'); } finally { setProbando(false); }
  };
  const toggleRevocadas = () => { const v = !incluirRevocadas; setIncluirRevocadas(v); onReload(v); };
  const visibles = conexiones;

  return (
    <div className="space-y-3" data-testid="oauth-conexiones">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">Conexiones de usuarios <span className="text-muted-foreground font-normal">({visibles.filter((c) => !c.revocado).length} activas)</span></p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={toggleRevocadas} data-testid="oauth-toggle-revocadas"><RefreshCw className="mr-1 h-3.5 w-3.5" /> {incluirRevocadas ? 'Ocultar revocadas' : 'Ver revocadas'}</Button>
          <Button size="sm" variant="outline" onClick={() => probar(null)} disabled={probando} data-testid="oauth-btn-probar">{probando ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="mr-1 h-3.5 w-3.5" />} Probar mi conexión</Button>
        </div>
      </div>
      {visibles.length === 0 ? (
        <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground" data-testid="oauth-conexiones-empty">Nadie ha autorizado todavía. Cuando un usuario conecte ChatGPT aparecerá aquí.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Usuario</th><th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">Permisos</th><th className="py-2 pr-3">Autorizada</th><th className="py-2 pr-3">Último uso</th><th className="py-2 pr-3">Llamadas</th><th className="py-2 pr-3">Estado</th><th className="py-2" /></tr></thead>
            <tbody>
              {visibles.map((c) => (
                <tr key={c.id} className="border-b last:border-0" data-testid={`oauth-conexion-row-${c.id}`}>
                  <td className="py-2 pr-3"><p className="font-medium">{c.user_nombre || c.email}</p><p className="text-xs text-muted-foreground">{c.email}</p></td>
                  <td className="py-2 pr-3 text-xs">{c.client_nombre}</td>
                  <td className="py-2 pr-3"><span className="text-xs" title={(c.scopes_labels || []).join('\n')}>{(c.scopes || []).length} permisos</span></td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{fmtFechaHora(c.created_at)}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{fmtFechaHora(c.last_used_at)}</td>
                  <td className="py-2 pr-3 text-xs">{c.total_requests || 0}</td>
                  <td className="py-2 pr-3">{c.revocado ? <Badge variant="secondary" title={c.revocado_motivo}>Revocada</Badge> : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Activa</Badge>}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {!c.revocado && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => probar(c.id)} data-testid={`oauth-conexion-probar-${c.id}`}><FlaskConical className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => revocar(c)} data-testid={`oauth-conexion-revocar-${c.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Revocar</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {prueba && (
        <div className="rounded-xl border bg-slate-50 p-3 text-xs" data-testid="oauth-prueba-resultado">
          <div className="flex items-center justify-between">
            <p className="font-semibold">{prueba.conexion ? `Conexión de ${prueba.conexion.email} · ${prueba.total_herramientas} herramientas visibles` : prueba.aviso}</p>
            <Button size="sm" variant="ghost" onClick={() => setPrueba(null)}>Cerrar</Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {prueba.herramientas.map((t) => (
              <Badge key={t.name} variant="outline" className={`text-[10px] ${t.lectura ? '' : 'border-amber-400 text-amber-700'} ${t.destructiva ? 'border-red-400 text-red-700' : ''}`} title={t.descripcion}>{t.name}</Badge>
            ))}
          </div>
          {prueba.ultima_llamada && <p className="mt-2 text-muted-foreground">Última llamada: {prueba.ultima_llamada.accion} · {fmtFechaHora(prueba.ultima_llamada.ts)} · {prueba.ultima_llamada.ok ? 'OK' : 'con error'}</p>}
        </div>
      )}
    </div>
  );
}
